import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentService } from '../services/appointmentService';
import type { Appointment } from '@clinic-os/shared/types';

export const APPOINTMENTS_KEY = ['appointments'] as const;

export function useAppointments() {
  return useQuery({
    queryKey: APPOINTMENTS_KEY,
    queryFn: () => appointmentService.getAll(),
  });
}

export function useAppointmentsByDate(date: Date) {
  return useQuery({
    queryKey: [...APPOINTMENTS_KEY, 'date', date.toISOString()],
    queryFn: () => appointmentService.getByDate(date),
  });
}

export function useUpcomingAppointments(limit = 10) {
  return useQuery({
    queryKey: [...APPOINTMENTS_KEY, 'upcoming', limit],
    queryFn: () => appointmentService.getUpcoming(undefined, limit),
  });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) =>
      appointmentService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: APPOINTMENTS_KEY }),
  });
}

export function useUpdateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Appointment> }) =>
      appointmentService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: APPOINTMENTS_KEY }),
  });
}
